import React, { HTMLAttributes } from 'react';
import { useComposedRefs } from '@radix-ui/react-compose-refs';

import { joinClassNames } from '../../modules/bem';
import * as font from '../../modules/font';
import * as text from '../../modules/text';
import { createComponent, WithNonInterferingComponent } from '../../modules/utils';
import { makeRefProp } from '../make-ref-prop-util';

export type TextProps<P extends object> = WithNonInterferingComponent<P, TextOwnProps> &
  HTMLAttributes<HTMLSpanElement> & {
    align?: text.Align;
    className?: string;
    color?: text.Color;
    /** @deprecated Use `ref` instead */
    elementRef?: React.Ref<any>;
    ellipsis?: boolean;
    fontFamily?: font.Family;
    fontWeight?: font.Weight;
    italic?: boolean;
    lineHeight?: font.LineHeight;
    size?: font.Size;
    transform?: text.Transform;
    wordBreak?: text.WordBreak;
  };

type TextOwnProps = {
  className: string;
};

export const Text = React.forwardRef(function Text<P extends object>(
  {
    align,
    className,
    color,
    component = 'span',
    elementRef,
    ellipsis,
    fontFamily,
    fontWeight,
    italic,
    lineHeight,
    size,
    transform,
    wordBreak,
    ...passProps
  }: TextProps<P>,
  ref: React.Ref<any>,
): React.ReactElement {
  const composedRef = useComposedRefs(elementRef, ref);

  const ownProps = {
    className: joinClassNames(
      className,
      text.alignClassName(align),
      text.colorClassName(color),
      text.ellipsisClassName(ellipsis),
      font.familyClassName(fontFamily),
      font.italicClassName(italic),
      font.lineHeightClassName(lineHeight),
      font.sizeClassName(size),
      font.weightClassName(fontWeight),
      text.transformClassName(transform),
      text.wordBreakClassName(wordBreak),
    ),
  };

  const refProp = makeRefProp(component, composedRef);

  return createComponent(component, {
    ...ownProps,
    ...passProps,
    ...refProp,
  });
}) as <P extends object>(props: TextProps<P> & { ref?: React.Ref<any> }) => React.ReactElement;
